import moment from 'moment';

class QueryPresenter {
  static THINKING_REGEX = /<think>([\s\S]*?)<\/think>/g;
  static THINKING_CONTENT_REGEX = /<think>([\s\S]*?)<\/think>/;

  constructor(data) {
    this.uuid = data.uuid;
    this.question = data.question;
    this.genAnswer = data.gen_answer;
    this.createdAt = data.created_at;
    this.completed = data.completed;
    this.shareToken = data.share_token; // Specific to QueryPresenter
    this.citations = data.gen_citations;
    this.relatedQuestions = data.gen_related_questions;
    this.publishedAt = data.published_at; // Optional, useful for subclasses
    this.slug = data.slug; // Optional, useful for subclasses
    this.paywalled = data.paywalled || false;
    this.paywall_reason = data.paywall_reason;
    this.deepResearch = data.deep_research || false;
  }

  checkValue(value) {
    return value ? value : 'N/A';
  }

  getUUID() {
    return this.checkValue(this.uuid);
  }

  getCreatedAt() {
    return moment(this.createdAt).format('YYYY-MM-DD HH:mm:ss');
  }

  getQuestion() {
    const question = this.checkValue(this.question);
    return this.formatQuestion(question);
  }

  formatQuestion(question) {
    return question; // Default behavior; subclasses can override
  }

  getAnswer() {
    return this.getAnswerWithoutThinking();
  }

  getAnswerWithThinking() {
    return this.genAnswer;
  }

  getAnswerWithoutThinking() {
    return this.genAnswer ? this.genAnswer.replace(QueryPresenter.THINKING_REGEX, '') : '';
  }

  getThinkingContent() {
    if (!this.genAnswer) return null;
    const match = this.genAnswer.match(QueryPresenter.THINKING_CONTENT_REGEX);
    return match ? match[1].trim() : null;
  }

  getCitations() {
    return this.citations;
  }

  getRelatedQuestions() {
    return this.relatedQuestions;
  }

  getPublishedAt(format = 'MMMM D, YYYY') {
    const date = moment(this.publishedAt);
    return date.isValid() ? date.format(format) : moment(new Date()).format(format);
  }

  isCompleted() {
    return Boolean(this.completed);
  }

  isPaywalled() {
    return this.paywalled || false;
  }

  paywallReason() {
    return this.paywall_reason || null;
  }

  isDeepResearch() {
    return Boolean(this.deepResearch);
  }

  isThinking() {
    if (!this.genAnswer) {
      // Return true if no answer and less than 15 minutes have passed since creation
      const fifteenMinutesAgo = moment().subtract(15, 'minutes');
      const createdAt = moment(this.createdAt);
      return createdAt.isAfter(fifteenMinutesAgo);
    }
    
    // Check if there's a <think> tag
    const thinkMatch = this.genAnswer.match(QueryPresenter.THINKING_CONTENT_REGEX);
    if (!thinkMatch) {
      // If there's no complete <think>...</think> pair, check if there's an opening <think> tag
      const hasOpeningThink = this.genAnswer.includes('<think>');
      return hasOpeningThink;
    }
    
    // Check if there's any content after the </think> tag
    const afterThinkMatch = this.genAnswer.match(/<\/think>([\s\S]*)/);
    if (!afterThinkMatch) return true;
    
    // Return true if there's only whitespace after </think>
    return afterThinkMatch[1].trim() === '';
  }

  hasAnswer() {
    return !!this.genAnswer;
  }

  getShareToken() {
    return this.shareToken;
  }

  getShareUrl() {
    return `${window.location.origin}/q/share/${this.getShareToken()}`;
  }
}

export default QueryPresenter;
